import { ref, computed, watch } from 'vue';
import store from '@/store';
import { isMobileOnly, isTablet, isBrowser } from 'mobile-device-detect';
import { useSgaEntrada } from '@/composables/front/checkout/useSgaEntrada';
import { t } from '@/utils/i18n-helper';

export function useDatosCompradoresSga(props, emit, proxy) {
    // ── State ──────────────────────────────────────────────────────────────────
    const localDatosCompradores = ref({ ...props.datos_comprador });
    const loading = ref(false);
    const errors = ref(null);
    const array_entradas = ref([]);
    const rellenar_datos_entradas = ref(false);
    const { applyNifReset } = useSgaEntrada(ref({}), () => {});
    const sga_eventos_cart_id = computed(() => parseInt(localStorage.getItem('sga_eventos_cart_id')));

    // ── Computed: Determinar si hay perfil SGA seleccionado ──
    const isSgaProfileLocked = computed(() => !!props.selectedProfile);

    // ── Computed: Campos que están bloqueados por el perfil ──
    const getLockedFields = () => {
        return isSgaProfileLocked.value ? ['email', 'email_confirmation', 'tipo_dni', 'dni'] : [];
    };

    // ── Computed: Validación de formulario (permite campos locked) ──
    const isFormValid = computed(() => {
        // Campos requeridos que SIEMPRE deben ser válidos
        const requiredFields = ['nombre', 'email', 'email_confirmation', 'tipo_dni', 'dni', 'cp'];
        
        // Si hay perfil bloqueado, estos campos ya están rellenos del perfil
        // así que podemos asumir que son válidos
        if (isSgaProfileLocked.value) {
            return true; // La validación ocurre en observer (ValidationObserver)
        }
        
        // Si no hay perfil, validar manualmente
        for (const field of requiredFields) {
            if (!localDatosCompradores.value[field]) {
                return false;
            }
        }
        
        return true;
    });

    añadirDispositivo();

    if (Array.isArray(props.items_carrito) && props.items_carrito.length > 0) {
        array_entradas.value = props.items_carrito.map(entrada => ({
            id: entrada.id,
            name: `${entrada.zona?.name || 'Entrada'} - Fila ${entrada.row} - Asiento ${entrada.seat}`,
            nombre: entrada.nombre ?? '',
            apellidos: entrada.apellidos ?? '',
            dni: entrada.dni ?? '',
            documento_tipo: entrada.documento_tipo ?? ''
        }));
    }

    // ── Watch: Auto-rellenar datos cuando selectedProfile cambia ──────────────
    watch(
        () => props.selectedProfile,
        newProfile => {
            if (newProfile) {
                // La API devuelve estructura anidada: { profile: { name, lastName, email, identification: { value, type } } }
                // con fallback a campos planos por compatibilidad
                const p = newProfile.profile || newProfile;
                localDatosCompradores.value.nombre = p.name || p.nombre || '';
                localDatosCompradores.value.apellidos = p.lastName || p.last_name || p.apellidos || '';
                localDatosCompradores.value.email = p.email || '';
                localDatosCompradores.value.email_confirmation = p.email || '';
                
                // Mapeo de Tipo de Documento: si issuerCode es ESP, forzamos DNI
                const issuerCode = p.identification?.issuerCode || p.issuer_code || '';
                if (issuerCode === 'ESP') {
                    localDatosCompradores.value.tipo_dni = 'DNI';
                } else {
                    localDatosCompradores.value.tipo_dni = p.identification?.type || p.tipo_dni || p.tipo_documento || '';
                }
                
                localDatosCompradores.value.dni = p.identification?.value || p.dni || '';
            }
        },
        { deep: true, immediate: true }
    );

    // ── Methods ────────────────────────────────────────────────────────────────
    function guardarDatos() {
        loading.value = true;
        errors.value = null;

        const cart_id = sga_eventos_cart_id.value;

        if (!cart_id) {
            proxy.$toast.error(t('front.sga.checkout.error_carrito'));
            loading.value = false;
            return;
        }

        const isNIF = localDatosCompradores.value.tipo_dni === 'NIF';
        const lockedFields = getLockedFields();

        const params = {
            cart_id: cart_id,
            tipo: 'eventos',
            event_session_id: props.sga_event_id,
            comprador: {
                name: localDatosCompradores.value.nombre,
                ...(!isNIF && { lastName: localDatosCompradores.value.apellidos }),
                email: localDatosCompradores.value.email,
                phone: localDatosCompradores.value.telefono,
                identification: {
                    type: localDatosCompradores.value.tipo_dni,
                    value: localDatosCompradores.value.dni,
                    issuerCode: 'ES'
                },
                cp : localDatosCompradores.value.cp
            },
            items: array_entradas.value.map((entrada, index) => ({
                id: entrada.id || index,
                name: entrada.nombre,
                ...(!isNIF && { lastName: entrada.apellidos }),
                identification: {
                    type: entrada.documento_tipo,
                    value: entrada.dni,
                    issuerCode: 'ES'
                },
                hasNotOwnIdentification: entrada.hasNotOwnIdentification,
                isMinor: entrada.isMinor
            }))
        };

        store.dispatch(proxy.config.getVuexAlias('sga.cart.guardar-datos'), params).then(() => {
            emit('fillDatosCompradores', localDatosCompradores.value, array_entradas.value);
            solicitarDatosCobro();
        }).catch(error => {
            loading.value = false;
            if (error.response?.data?.errors) {
                errors.value = error.response.data.errors;
            } else {
                proxy.$toast.error(error.response?.data?.message || t('front.sga.checkout.error_guardar_datos'));
            }
        });
    }

    function solicitarDatosCobro() {
        const params = {
            filters: {},
            cart_id: sga_eventos_cart_id.value
        };

        store.dispatch(proxy.config.getVuexAlias('sga.cart.solicitar-cobro'), params).then(res => {
            emit('setDatosCobro', res.data.jet_id, res.data.formulario, res.data.session_id, res.data.cart_id);
        }).catch(error => {
            proxy.$toast.error(error.response?.data?.message || t('front.sga.checkout.error_cobro'));
        }).finally(() => {
            loading.value = false;
        });
    }

    function cambiarAsiento() {
        emit('volver');
    }

    function añadirDispositivo() {
        if (isMobileOnly) localDatosCompradores.value.dispositivo = 0;
        if (isTablet) localDatosCompradores.value.dispositivo = 1;
        if (isBrowser) localDatosCompradores.value.dispositivo = 2;
    }

    function setPhoneNumber(number) {
        if (number && number !== undefined && !isNaN(number)) {
            localDatosCompradores.value.telefono = number;
        }
    }

    function sincronizarPrimeraEntrada(sincronizar) {
        if (array_entradas.value[0]) {
            const updated = applyNifReset({
                ...array_entradas.value[0],
                nombre: sincronizar ? localDatosCompradores.value.nombre : null,
                apellidos: sincronizar ? localDatosCompradores.value.apellidos : null,
                dni: sincronizar ? localDatosCompradores.value.dni : null,
                documento_tipo: sincronizar ? localDatosCompradores.value.tipo_dni : null,
            }, sincronizar);
            array_entradas.value.splice(0, 1, updated);
        }
    }

    return {
        localDatosCompradores,
        loading,
        errors,
        array_entradas,
        rellenar_datos_entradas,
        sga_eventos_cart_id,
        isSgaProfileLocked,
        getLockedFields,
        isFormValid,
        guardarDatos,
        solicitarDatosCobro,
        cambiarAsiento,
        setPhoneNumber,
        sincronizarPrimeraEntrada,
    };
}
